import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useStore } from '../hooks/useStore';
import { fetchJobs } from '../lib/api';
import { safeArray } from '../lib/safe';

export const Route = createFileRoute('/jobs')({
  component: JobsPage,
});

const WRITEBACK_STYLES: Record<string, { bg: string; label: string }> = {
  applied: { bg: 'bg-emerald-50 text-emerald-700', label: 'Applied' },
  pending_review: { bg: 'bg-amber-50 text-amber-700', label: 'Pending Review' },
  skipped: { bg: 'bg-slate-100 text-slate-500', label: 'Skipped' },
  rolled_back: { bg: 'bg-red-50 text-red-700', label: 'Rolled Back' },
};

function shopShortCode(shop: string): string {
  if (!shop) return '\u2014';
  if (shop.includes('casual-chic-boutique')) return 'CCB';
  if (shop.includes('casual-chic-luxe')) return 'CCL';
  if (shop.includes('casual-chic-collection')) return 'CCC';
  if (shop.includes('q0gtxj')) return 'CCS';
  if (shop.includes('bargains')) return 'TBD';
  return shop.split('.')[0];
}

function JobsPage() {
  const { activeStore } = useStore();

  const { data, isLoading } = useQuery({
    queryKey: ['jobs', activeStore.shop],
    queryFn: () => fetchJobs(activeStore.shop),
    refetchInterval: 5_000,
  });

  const jobs = safeArray(data?.jobs);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-[var(--ccb-text)]">
        Jobs
        {jobs.length > 0 && <span className="text-sm font-normal text-[var(--ccb-text-secondary)] ml-2">({jobs.length})</span>}
      </h2>

      <div className="bg-white rounded-xl border border-[var(--ccb-border)] overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">Loading jobs...</div>
        ) : jobs.length === 0 ? (
          <div className="p-12 text-center text-sm text-[var(--ccb-text-secondary)]">No recent jobs</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--ccb-border)]">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Product</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Store</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Writeback</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Started</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--ccb-text-secondary)] uppercase tracking-wide">Error</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job: any) => {
                  const statusColor =
                    job.status === 'processing' || job.status === 'pending'
                      ? 'bg-blue-50 text-blue-700'
                      : job.status === 'completed'
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-red-50 text-red-700';

                  const wb = WRITEBACK_STYLES[job.writebackStatus] || null;

                  return (
                    <tr key={job.id} className="border-b border-[var(--ccb-border)] last:border-0 hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor}`}>{job.status}</span>
                      </td>
                      <td className="px-4 py-3 max-w-[250px]">
                        {job.productId ? (
                          <Link
                            to="/products/$productId"
                            params={{ productId: String(job.productId) }}
                            className="font-medium text-[var(--ccb-text)] hover:text-[var(--ccb-primary)] transition-colors truncate block"
                          >
                            {job.title}
                          </Link>
                        ) : (
                          <span className="font-medium text-[var(--ccb-text)] truncate block">{job.title}</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-[var(--ccb-text-secondary)]">{shopShortCode(job.shop || '')}</span>
                      </td>
                      <td className="px-4 py-3 text-[var(--ccb-text-secondary)] capitalize">{job.type}</td>
                      <td className="px-4 py-3">
                        {wb ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${wb.bg}`}>{wb.label}</span>
                        ) : (
                          <span className="text-xs text-slate-400">{'\u2014'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--ccb-text-secondary)]">
                        {job.startedAt ? new Date(job.startedAt).toLocaleString() : '\u2014'}
                      </td>
                      <td className="px-4 py-3 text-xs text-red-600 truncate max-w-[250px]">
                        {job.errorMessage || '\u2014'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
